import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from './FirebaseConfig.js';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { persistVisitUtms, readVisitUtms, withVisitUtms } from './utm';

import ecLogoLight from './assets/EC-LogoFullColor.svg';
import ecLogoDark from './assets/EC-LogoFullColor-White.svg';

import { ReactComponent as FacebookIcon } from './assets/social-media-icons/facebook.svg';
import { ReactComponent as InstagramIcon } from './assets/social-media-icons/instagram.svg';
import { ReactComponent as XIcon } from './assets/social-media-icons/x.svg';
import { ReactComponent as YoutubeIcon } from './assets/social-media-icons/youtube.svg';
import { ReactComponent as TiktokIcon } from './assets/social-media-icons/tiktok.svg';

import { ReactComponent as VIPIcon } from './assets/tapscan-icons/new-here-glyph.svg';
import { ReactComponent as GivingIcon } from './assets/tapscan-icons/giving-glyph.svg';
import { ReactComponent as SalvationIcon } from './assets/tapscan-icons/decision-for-christ-glyph.svg';
import { ReactComponent as GetConnectedIcon } from './assets/tapscan-icons/get-connected-glyph.svg';
import { ReactComponent as PrayerIcon } from './assets/tapscan-icons/need-prayer-glyph.svg';

function campusFromPath() {
    return window.location.pathname.split('/')[2] || 'default';
}

function campusDisplayName(slug) {
    if (!slug || slug === 'default') {
        return '';
    }

    return slug
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

const SOCIAL_PLATFORMS = [
    {
        id: 'facebook',
        fallbackHref: 'https://www.facebook.com/elevationchurch.org/',
        label: 'Facebook',
        Icon: FacebookIcon,
    },
    {
        id: 'instagram',
        fallbackHref: 'https://www.instagram.com/elevationchurch/',
        label: 'Instagram',
        Icon: InstagramIcon,
    },
    {
        id: 'x',
        fallbackHref: 'https://twitter.com/elevationchurch',
        label: 'X / Twitter',
        Icon: XIcon,
    },
    {
        id: 'youtube',
        fallbackHref: 'https://www.youtube.com/user/ElevationOnline',
        label: 'YouTube',
        Icon: YoutubeIcon,
    },
    {
        id: 'tiktok',
        fallbackHref: 'https://www.tiktok.com/@elevationchurch',
        label: 'TikTok',
        Icon: TiktokIcon,
    },
];

function campusHref(linkMap, campus, fallbackHref) {
    return (linkMap && (linkMap[campus] || linkMap.default)) || fallbackHref;
}

function HomeHeader({ campus, visitUtms }) {
    const campusLabel = campusDisplayName(campus);

    return (
        <div className="w-full py-4 px-6 flex flex-row justify-between items-center gap-4">
            <a className="shrink-0 w-fit h-fit block" href={withVisitUtms('https://elevationchurch.org/', visitUtms)} target="_blank" rel="noreferrer">
                <picture>
                    <source srcSet={ecLogoDark} media="(prefers-color-scheme: dark)" />
                    <img className="w-32" src={ecLogoLight} alt="Elevation Church" />
                </picture>
            </a>
            {campusLabel ? (
                <p className="min-w-0 max-w-[50%] text-right whitespace-normal [overflow-wrap:anywhere] font-[family-name:'DM_Sans',sans-serif] text-base sm:text-xl font-semibold leading-snug tracking-[0.009375rem] text-[#666666] dark:text-white/60">
                    {campusLabel}
                </p>
            ) : null}
        </div>
    );
}

function Home() {
    const [data, setData] = useState([]);
    const [topData, setTopData] = useState([]);
    const [campus, setCampus] = useState(campusFromPath);
    const [isLoading, setIsLoading] = useState(true);
    const [socialLinkMaps, setSocialLinkMaps] = useState({});
    const [visitUtms] = useState(readVisitUtms);

    const analytics = getAnalytics();

    const iconMap = {
        vip: VIPIcon,
        getConnected: GetConnectedIcon,
        giving: GivingIcon,
        salvation: SalvationIcon,
        prayer: PrayerIcon,
    };

    const socialLinks = SOCIAL_PLATFORMS.map(({ id, fallbackHref, label, Icon }) => ({
        href: campusHref(socialLinkMaps[id], campus, fallbackHref),
        label,
        Icon,
    }));

    useEffect(() => {
        persistVisitUtms(visitUtms);
    }, [visitUtms]);

    useEffect(() => {
        const currentCampus = campusFromPath();
        const isOnline = currentCampus === 'online';
        setCampus(currentCampus);

        const getTopData = onSnapshot(
            query(collection(db, 'online'), where(isOnline ? 'visible' : 'campusVisible', '==', true)),
            (snapshot) => {
            console.log(snapshot);

            if (snapshot.empty) {
                setIsLoading(false);
                return;
            }

            let snapshotData = snapshot.docs.map((res) => ({
                ...res.data(),
                id: res.id,
            }));
            snapshotData.sort((a, b) => a.order - b.order);

            console.log(snapshotData);
            setTopData(snapshotData);
            setIsLoading(false);
        });

        const getData = onSnapshot(
            query(collection(db, 'global'), where(isOnline ? 'visibleOnline' : 'visible', '==', true)),
            (snapshot) => {
            console.log(snapshot);

            if (snapshot.empty) {
                return;
            }

            let snapshotData = snapshot.docs.map((res) => ({
                ...res.data(),
                id: res.id,
            }));
            snapshotData.sort((a, b) => (isOnline ? a.orderOnline - b.orderOnline : a.order - b.order));

            console.log(snapshotData);
            setData(snapshotData);
        });

        const checkSettings = onSnapshot(doc(db, 'globalSettings/settings'), (snapshot) => {
            if (snapshot.data()?.bypass) {
                window.location.replace(snapshot.data()?.bypassURL);
            } else {
                setIsLoading(false);
            }
        });

        const getSocialLinks = onSnapshot(collection(db, 'socialLinks'), (snapshot) => {
            const next = {};
            (snapshot.docs || []).forEach((res) => {
                next[res.id] = res.data()?.link;
            });
            setSocialLinkMaps(next);
        });

        return () => {
            getTopData();
            getData();
            checkSettings();
            getSocialLinks();
        };
    }, []);

    if (isLoading) {
        return (
            <div className="w-full min-w-0 min-h-screen bg-white text-black/[0.87] dark:bg-[#0a0a0a] dark:text-white">
                <div className="w-full min-h-screen flex flex-col justify-start items-center">
                    <HomeHeader campus={campus} visitUtms={visitUtms} />
                    <div className="w-full h-fit">
                        <div className="w-full h-[1px] bg-[#e0e0e0] dark:bg-white/[0.12]" />
                    </div>
                    <div className="w-full min-w-0 max-w-[75rem] px-[clamp(1.5rem,5vw,4rem)] py-8 grid grid-cols-2 max-[320px]:grid-cols-1 gap-4 md:flex md:items-stretch">
                        {[0, 1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="flex flex-1 min-w-0 flex-col items-start gap-2 p-3 bg-[#f4f4f4] dark:bg-[#1a1a1a] border border-black/50 dark:border-white/50 rounded-2xl animate-pulse"
                            >
                                <span className="w-[2.375rem] h-[2.375rem] bg-white dark:bg-[#272727] rounded-xl" />
                                <span className="h-[1.375rem] w-24 bg-black/10 dark:bg-white/10 rounded" />
                            </div>
                        ))}
                    </div>
                    <div className="w-full min-w-0 max-w-[75rem] px-[clamp(1.5rem,5vw,4rem)] pb-5 grid grid-cols-1 md:grid-cols-[repeat(2,minmax(15rem,1fr))] gap-x-4">
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="flex min-w-0 flex-col py-6 overflow-hidden rounded-2xl animate-pulse">
                                <div className="w-full aspect-[71/40] bg-[#f4f4f4] dark:bg-[#1a1a1a] rounded-2xl" />
                                <div className="min-w-0 px-4 pt-4 pb-2 flex flex-col gap-2">
                                    <div className="h-6 w-2/3 bg-black/10 dark:bg-white/10 rounded" />
                                    <div className="h-4 w-full bg-black/10 dark:bg-white/10 rounded" />
                                </div>
                            </div>
                        ))}
                    </div>
                    <h3 className="font-normal text-xs mt-auto pb-8 text-black/[0.31] dark:text-white/60">
                        © 2024-2026 Elevation Church. <span className="font-medium">All Rights Reserved.</span>
                    </h3>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full min-w-0 min-h-screen bg-white text-black/[0.87] dark:bg-[#0a0a0a] dark:text-white">
            <div className="w-full min-h-screen flex flex-col justify-start items-center">
                <HomeHeader campus={campus} visitUtms={visitUtms} />
                <div className="w-full h-fit">
                    <div className="w-full h-[1px] bg-[#e0e0e0] dark:bg-white/[0.12]" />
                </div>
                <div className="w-full min-w-0 max-w-[75rem] px-[clamp(1.5rem,5vw,4rem)] py-8 grid grid-cols-2 max-[320px]:grid-cols-1 gap-4 md:flex md:items-stretch">
                    {topData.map((dat) => {
                        const Icon = iconMap[dat.iconKey];
                        return (
                            <a
                                className="flex flex-1 min-w-0 flex-col items-start gap-2 p-3 [overflow-wrap:anywhere] bg-[#f4f4f4] dark:bg-[#1a1a1a] border border-black/50 dark:border-white/50 rounded-2xl"
                                href={withVisitUtms(dat.link[campus] || dat.link['default'], visitUtms)}
                                target="_blank"
                                rel="noreferrer"
                                key={dat.id}
                                onClick={() =>
                                    logEvent(analytics, 'button_click', {
                                        button_id: dat.id,
                                        button_title: dat.description,
                                        page: campus === 'online' ? 'online' : 'home',
                                    })
                                }
                            >
                                <span className="flex shrink-0 w-[2.375rem] h-[2.375rem] items-center justify-center overflow-hidden text-black/[0.87] bg-white dark:text-white dark:bg-[#272727] rounded-xl [&_svg]:w-full [&_svg]:h-full [&_svg]:fill-current [&_svg_path]:fill-current">
                                    {Icon && <Icon role="img" aria-label={dat.description} />}
                                </span>
                                <span className="min-w-0 max-w-full font-[family-name:system-ui,-apple-system,BlinkMacSystemFont,'SF_Pro_Text',sans-serif] text-[1.0625rem] font-[590] leading-[1.375rem] tracking-[-0.026875rem] whitespace-normal [overflow-wrap:anywhere]">
                                    {dat.description}
                                </span>
                            </a>
                        );
                    })}
                </div>

                <div className="w-full min-w-0 max-w-[75rem] px-[clamp(1.5rem,5vw,4rem)] pb-5 grid grid-cols-1 md:grid-cols-[repeat(2,minmax(15rem,1fr))] gap-x-4">
                    {data.map((dat) => {
                        return (
                            <a
                                key={dat.id}
                                className={`${dat.id} flex min-w-0 flex-col py-6 overflow-hidden rounded-2xl`}
                                href={withVisitUtms(dat.link[campus] || dat.link['default'], visitUtms)}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() =>
                                    logEvent(analytics, 'button_click', {
                                        button_id: dat.id,
                                        button_title: dat.title,
                                        page: campus === 'online' ? 'online' : 'home',
                                    })
                                }
                            >
                                <img className="block w-full aspect-[71/40] object-cover rounded-2xl" src={dat.bgImage} alt={dat.title || ''} />
                                {dat.title !== '' || dat.description !== '' ? (
                                    <div className="min-w-0 px-4 pt-4 pb-2 font-[family-name:'DM_Sans',sans-serif]">
                                        {dat.title !== '' && (
                                            <h1 className="text-xl font-bold leading-[1.33] tracking-[0.009375rem] whitespace-normal [overflow-wrap:anywhere] text-[#212121] dark:text-white">
                                                {dat.title}
                                            </h1>
                                        )}
                                        {dat.description !== '' && (
                                            <h2 className="text-sm font-normal leading-[1.43] tracking-[0.010625rem] whitespace-normal [overflow-wrap:anywhere] text-[#666666] dark:text-white/60">
                                                {dat.description}
                                            </h2>
                                        )}
                                    </div>
                                ) : null}
                            </a>
                        );
                    })}
                </div>
                <div className="w-full px-5 md:px-16 flex flex-col justify-center items-center md:pt-8 lg:pt-8 gap-5 mt-auto">
                    <div className="w-full flex flex-row justify-center items-center gap-5">
                        {socialLinks.map(({ href, label, Icon }) => (
                            <a
                                key={label}
                                className="flex flex-1 min-w-0 max-w-8 aspect-square items-center justify-center text-black/[0.87] dark:text-white bg-[#f4f4f4] dark:bg-[#1a1a1a] rounded-full"
                                href={withVisitUtms(href, visitUtms)}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Icon role="img" aria-label={label} className="w-4 h-4 fill-current" />
                            </a>
                        ))}
                    </div>
                    <h3 className="font-normal text-xs text-center pb-5 text-black/[0.31] dark:text-white/60">
                        © 2024-2026 Elevation Church. <span className="font-medium">All Rights Reserved.</span>
                    </h3>
                </div>
            </div>
        </div>
    );
}

export default Home;
